<template>
  <SlottedRow
    :uuid="displayField.uuid"
    aria-label="New Field Row"
  >
    <template #label>
      <div class="new-field-label-wrapper">
        <FieldSuggestions
          :id="`suggestions-${displayField.uuid}`"
          ref="suggestions"
          :data-testid="`suggestions-${displayField.uuid}`"
          :suggestions="suggestions"
          :current-selection="displayField.label"
          :editing="true"
          placeholder="New Custom Field"
          @update-selection="handleUpdateLabel"
        />
      </div>
    </template>
    <template #value>
      <div class="new-field-value-wrapper">
        <DisplayFieldTextarea
          :id="`value-${displayField.uuid}`"
          ref="value"
          v-model="displayField.value"
          :data-testid="`new-value-field-${displayField.uuid}`"
          :editing="true"
          @focusout="handleFocusOut"
          @keydown.tab.exact.prevent="checkRow(); setFocusToLabel()"
        />
      </div>
    </template>
  </SlottedRow>
</template>

<script>
import SlottedRow from './SlottedRow.vue';
import {DisplayField} from '@/lib/classes/InfoWindowContent/DisplayField';
import DisplayFieldTextarea from './DisplayFieldTextarea.vue';
import {mapState} from 'vuex';
import FieldSuggestions from './FieldSuggestions.vue';
import {isHyperLink} from '@/lib/ReusableFunctions/Wrappers';

const createBlankDisplayField = () => new DisplayField({
  label: '',
  value: '',
  unit: 'string',
  allowEdit: true,
  allowLabelEdit: true,
  allowDelete: true},
);

export default {
  name: 'NewFieldRow',
  components: {
    SlottedRow,
    DisplayFieldTextarea,
    FieldSuggestions,
  },
  inject: ['contentType'],
  props: {
    compGroupType: {
      type: String,
      default: '',
    },
  },
  emits: ['addDisplayField'],
  data() {
    return {
      suggestions: [],
      iwType: null,
      displayField: createBlankDisplayField(),
    };
  },
  computed: {
    ...mapState('infowindow', ['default', 'fieldSuggestions']),
  },
  created() {
    if (this.compGroupType !== '') {
      this.suggestions = this.fieldSuggestions[this.compGroupType] ?? [];
      return;
    }

    this.suggestions = this.fieldSuggestions[this.contentType] ?? [];
  },
  methods: {
    async handleFocusOut(event) {
      this.checkRow();
      if (event.relatedTarget?.id === undefined) {
        this.setFocusToLabel();
      }
    },
    handleUpdateLabel(newLabel) {
      this.displayField.label = newLabel;
    },
    /**
     * Focuses on the input field for the `Suggestions` component.
     */
    setFocusToLabel() {
      setTimeout(() => {
        const ref = this.$refs.suggestions;
        if (ref) {
          const el = ref.$el.querySelector('input');
          el.focus();
          el.click();
        }
      });
    },
    /**
     * Focuses on the input field for the value of the custom `DisplayField`.
     */
    setFocusToValue() {
      setTimeout(() => {
        /** @type {HTMLElement} */
        const valueTextarea = this.$refs['value'].$el.querySelector('textarea');
        valueTextarea.focus();
        valueTextarea.click();
      });
    },
    checkRow() {
      // Check if the form is valid
      if (this.displayField.label === '') {
        return;
      }

      if (isHyperLink(this.displayField.value)) {
        this.displayField.unit = 'link';
      }

      // If valid, make a display field and emit an event
      this.$emit('addDisplayField', this.displayField);

      // Reset the form
      this.displayField = createBlankDisplayField();
    },
  },
};
</script>
<style lang="scss" scoped></style>
